/**
 * Rewrites the date metadata inside photo/video file bytes so that when the
 * file is saved to the user's camera roll, the OS gallery treats it as a
 * newly-captured asset (sorted at "now") instead of the original capture date.
 *
 * Why: iOS Photos sorts by EXIF DateTimeOriginal (falling back to file mtime),
 * and Android MediaStore reads the same EXIF tag for DATE_TAKEN at insert.
 * Without rewriting these fields, downloaded photos appear buried at the date
 * the photo was originally taken — months or years ago — which is the bug.
 *
 * Scope:
 *   - JPEG: rewrite DateTime / DateTimeOriginal / DateTimeDigitized in-place.
 *     These are 20-byte ASCII strings ("YYYY:MM:DD HH:MM:SS\0") so the EXIF
 *     segment length doesn't change.
 *   - MP4 / MOV: rewrite mvhd + every tkhd creation_time and modification_time.
 *     Atom sizes do not change.
 *   - Anything else (PNG, HEIC, WEBP, GIF, AVI, etc.): bytes unchanged, but
 *     the returned Blob is freshly constructed and the wrapping File will use
 *     a current lastModified — which Android MediaStore falls back to when
 *     EXIF is absent.
 *
 * Intentionally implemented without external dependencies. The repo restricts
 * adding npm packages mid-session, and JPEG EXIF date / MP4 atom timestamps
 * are simple enough to rewrite directly.
 */

const JPEG_TYPES = new Set(['image/jpeg', 'image/jpg', 'image/pjpeg']);
const MP4_TYPES = new Set([
  'video/mp4',
  'video/quicktime',
  'video/x-m4v',
  'video/3gpp',
  'video/3gpp2',
]);

const JPEG_EXTS = /\.(jpe?g)$/i;
const MP4_EXTS = /\.(mp4|m4v|mov|3gp|3g2)$/i;

function isJpegFile(filename: string, contentType?: string): boolean {
  if (contentType && JPEG_TYPES.has(contentType.toLowerCase())) return true;
  return JPEG_EXTS.test(filename);
}

function isMp4Family(filename: string, contentType?: string): boolean {
  if (contentType && MP4_TYPES.has(contentType.toLowerCase())) return true;
  return MP4_EXTS.test(filename);
}

/** Format a Date as the 19-byte EXIF ASCII string "YYYY:MM:DD HH:MM:SS". */
function formatExifDateTime(when: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${when.getFullYear()}:${pad(when.getMonth() + 1)}:${pad(when.getDate())} ` +
    `${pad(when.getHours())}:${pad(when.getMinutes())}:${pad(when.getSeconds())}`
  );
}

/** Seconds between 1904-01-01 UTC and 1970-01-01 UTC — the MP4 epoch offset. */
const MP4_EPOCH_OFFSET_SECONDS = 2_082_844_800;

/**
 * Rewrites EXIF DateTime / DateTimeOriginal / DateTimeDigitized tags in a
 * JPEG buffer. Returns the same buffer when no EXIF segment is present.
 *
 * All three date tags use a fixed 20-byte ASCII format with a trailing NUL,
 * so we can patch the bytes in place without recomputing segment lengths or
 * IFD offsets — which avoids the bug class where shifting tag values corrupts
 * downstream EXIF data (orientation, GPS, thumbnail pointer, etc.).
 */
export function stampJpegAsNew(input: ArrayBuffer, when: Date = new Date()): ArrayBuffer {
  const view = new DataView(input);
  if (view.byteLength < 4 || view.getUint16(0) !== 0xffd8) {
    return input;
  }

  let cursor = 2;
  while (cursor + 4 <= view.byteLength) {
    const marker = view.getUint16(cursor);
    if (marker === 0xffda) break;
    if ((marker & 0xff00) !== 0xff00) break;

    const segmentLength = view.getUint16(cursor + 2);
    if (segmentLength < 2) break;
    const segmentEnd = cursor + 2 + segmentLength;
    if (segmentEnd > view.byteLength) break;

    if (marker === 0xffe1) {
      const exifHeaderStart = cursor + 4;
      const tiffStart = exifHeaderStart + 6;
      const headerOk =
        view.getUint32(exifHeaderStart) === 0x45786966 &&
        view.getUint16(exifHeaderStart + 4) === 0x0000;
      if (headerOk && tiffStart + 8 <= segmentEnd) {
        rewriteExifDatesInPlace(input, tiffStart, segmentEnd, when);
      }
    }

    cursor = segmentEnd;
  }

  return input;
}

function rewriteExifDatesInPlace(
  buf: ArrayBuffer,
  tiffStart: number,
  segmentEnd: number,
  when: Date,
): void {
  const view = new DataView(buf);
  const byteOrder = view.getUint16(tiffStart);
  let littleEndian: boolean;
  if (byteOrder === 0x4949) littleEndian = true;
  else if (byteOrder === 0x4d4d) littleEndian = false;
  else return;

  if (view.getUint16(tiffStart + 2, littleEndian) !== 0x002a) return;
  const ifd0Offset = view.getUint32(tiffStart + 4, littleEndian);
  if (ifd0Offset === 0 || tiffStart + ifd0Offset + 2 > segmentEnd) return;

  const dateAscii = formatExifDateTime(when);
  const encoder = new TextEncoder();
  const dateBytes = encoder.encode(dateAscii);
  if (dateBytes.length !== 19) return;

  const writeDateAt = (offsetWithinTiff: number) => {
    const absolute = tiffStart + offsetWithinTiff;
    if (absolute + 20 > segmentEnd) return;
    const dst = new Uint8Array(buf, absolute, 20);
    dst.set(dateBytes, 0);
    dst[19] = 0;
  };

  const walkIfd = (ifdRelOffset: number, depth: number): { exifIfd?: number } => {
    if (depth > 2) return {};
    const ifdAbs = tiffStart + ifdRelOffset;
    if (ifdAbs + 2 > segmentEnd) return {};
    const count = view.getUint16(ifdAbs, littleEndian);
    const result: { exifIfd?: number } = {};

    for (let i = 0; i < count; i++) {
      const entry = ifdAbs + 2 + i * 12;
      if (entry + 12 > segmentEnd) break;

      const tag = view.getUint16(entry, littleEndian);
      const type = view.getUint16(entry + 2, littleEndian);
      const valueCount = view.getUint32(entry + 4, littleEndian);

      const isDateAscii = type === 2 && valueCount === 20;
      const isDateTag = tag === 0x0132 || tag === 0x9003 || tag === 0x9004;

      if (isDateTag && isDateAscii) {
        const valueOffset = view.getUint32(entry + 8, littleEndian);
        writeDateAt(valueOffset);
      } else if (tag === 0x8769 && type === 4 && valueCount === 1) {
        result.exifIfd = view.getUint32(entry + 8, littleEndian);
      }
    }
    return result;
  };

  const ifd0 = walkIfd(ifd0Offset, 0);
  if (ifd0.exifIfd !== undefined) {
    walkIfd(ifd0.exifIfd, 1);
  }
}

/**
 * Rewrites mvhd + every tkhd creation_time and modification_time inside an
 * MP4 / QuickTime / 3GP container so OS galleries treat the file as new.
 *
 * Apple's `com.apple.quicktime.creationdate` metadata atom (variable-length
 * ISO 8601 string) is intentionally NOT rewritten here — it would require
 * shifting downstream atom offsets. iOS Photos falls back to mvhd
 * creation_time when that string is absent or older, which is sufficient
 * for the "show as new" goal.
 */
export function stampMp4AsNew(input: ArrayBuffer, when: Date = new Date()): ArrayBuffer {
  if (input.byteLength < 16) return input;
  const view = new DataView(input);
  const nowMp4 = Math.floor(when.getTime() / 1000) + MP4_EPOCH_OFFSET_SECONDS;
  if (nowMp4 < 0 || nowMp4 > 0xffffffff) {
    // Out of v0 32-bit range — only happens far past year 2040; v1 atoms
    // are handled below as 64-bit writes.
  }

  const walkContainer = (start: number, end: number, depth: number): void => {
    if (depth > 6) return;
    let cursor = start;
    while (cursor + 8 <= end) {
      let size = view.getUint32(cursor);
      const type =
        String.fromCharCode(view.getUint8(cursor + 4)) +
        String.fromCharCode(view.getUint8(cursor + 5)) +
        String.fromCharCode(view.getUint8(cursor + 6)) +
        String.fromCharCode(view.getUint8(cursor + 7));

      let headerSize = 8;
      if (size === 1) {
        if (cursor + 16 > end) break;
        const hi = view.getUint32(cursor + 8);
        const lo = view.getUint32(cursor + 12);
        size = hi * 0x100000000 + lo;
        headerSize = 16;
      } else if (size === 0) {
        size = end - cursor;
      }
      if (size < headerSize || cursor + size > end) break;

      const bodyStart = cursor + headerSize;
      const bodyEnd = cursor + size;

      if (type === 'moov' || type === 'trak' || type === 'mdia' || type === 'edts') {
        walkContainer(bodyStart, bodyEnd, depth + 1);
      } else if (type === 'mvhd' || type === 'tkhd' || type === 'mdhd') {
        rewriteFullBoxTimestamps(view, bodyStart, bodyEnd, nowMp4, when);
      }

      cursor = bodyEnd;
    }
  };

  walkContainer(0, view.byteLength, 0);
  return input;
}

function rewriteFullBoxTimestamps(
  view: DataView,
  bodyStart: number,
  bodyEnd: number,
  nowMp4Seconds: number,
  when: Date,
): void {
  if (bodyStart + 4 > bodyEnd) return;
  const version = view.getUint8(bodyStart);

  if (version === 0) {
    if (bodyStart + 12 > bodyEnd) return;
    const clamped = Math.max(0, Math.min(0xffffffff, nowMp4Seconds));
    view.setUint32(bodyStart + 4, clamped);
    view.setUint32(bodyStart + 8, clamped);
  } else if (version === 1) {
    if (bodyStart + 20 > bodyEnd) return;
    const seconds = Math.floor(when.getTime() / 1000) + MP4_EPOCH_OFFSET_SECONDS;
    const hi = Math.floor(seconds / 0x100000000);
    const lo = seconds >>> 0;
    view.setUint32(bodyStart + 4, hi);
    view.setUint32(bodyStart + 8, lo);
    view.setUint32(bodyStart + 12, hi);
    view.setUint32(bodyStart + 16, lo);
  }
}

/**
 * Returns a new Blob whose contents have been re-stamped to "now" so the
 * file appears as a fresh capture in the user's camera roll. The caller
 * should then wrap the Blob in a File with a current lastModified for the
 * download <a> or share sheet — both flows in AttachmentViewerModal already
 * do this naturally.
 *
 * For unsupported formats the original blob bytes are returned unchanged
 * (still wrapped in a fresh Blob so any caller-side caches are bypassed).
 */
export async function stampMediaAsNew(
  blob: Blob,
  filename: string,
  contentType?: string,
  when: Date = new Date(),
): Promise<Blob> {
  const effectiveType = contentType || blob.type || 'application/octet-stream';

  try {
    if (isJpegFile(filename, effectiveType)) {
      const buf = await blob.arrayBuffer();
      const copy = buf.slice(0);
      stampJpegAsNew(copy, when);
      return new Blob([copy], { type: effectiveType });
    }

    if (isMp4Family(filename, effectiveType)) {
      const buf = await blob.arrayBuffer();
      const copy = buf.slice(0);
      stampMp4AsNew(copy, when);
      return new Blob([copy], { type: effectiveType });
    }
  } catch (err) {
    // Metadata rewrite is best-effort: if anything goes wrong (corrupt
    // EXIF, exotic container, etc.) fall through and hand back the
    // original bytes so the user still gets their file.
    console.warn('[mediaMetadata] rewrite failed, returning original bytes', err);
  }

  return new Blob([blob], { type: effectiveType });
}
